import type { Theme } from '@material-ui/core/styles'
import { makeStyles } from '@material-ui/core/styles'
import React, { useContext, useState } from 'react'

import Typography from '@material-ui/core/Typography'

import classnames from 'classnames'

import { Button, ConfirmationDialog, Grid, Switch, useClientOptions, useDialogs } from '@paintscout/ui'

import {
  EditPresentationSectionDialog,
  OptionsTileList,
  PresentationSelectDialog,
  QuoteContext
} from '@paintscout/react-quote'

import {
  getFeature,
  getInitialPresentation,
  getObjectLabels,
  getPresentationOptions,
  getQuotePresentation,
  getQuoteTemplateOptions,
  getQuoteType,
  getQuoteTypeOption
} from '@paintscout/util/builder'
import findIndex from 'lodash/findIndex'
import { useFlags } from 'shared/components/common/FlagsProvider'
import type { PresentationOption, PresentationPage, PresentationSection } from 'paintscout'

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {},
    meta: {
      padding: theme.spacing(2),
      background: theme.paintscout.palette.brand.Neutral.P96
    },
    pages: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    },

    tiles: {
      gridTemplateColumns: 'repeat(1, 1fr)',
      paddingRight: 0,
      paddingBottom: 0
    },
    button: {
      whiteSpace: 'normal',
      textAlign: 'left',
      justifyContent: 'flex-start',
      height: 'auto',

      padding: `${theme.typography.pxToRem(5)} 0`,
      paddingTop: 0,
      borderRadius: 0,
      borderBottom: `2px solid transparent`,
      '&:hover': {
        color: theme.palette.primary.main,
        background: 'transparent'
      }
    },
    currentPage: {
      color: theme.palette.primary.main,
      borderBottomColor: theme.palette.primary.main
    },
    hidden: {
      visibility: 'hidden'
    },
    buttonWrapper: {
      marginTop: theme.spacing(2)
    },
    textDiv: {
      overflow: 'hidden',
      width: '160px'
    }
  }
})

export interface QuotePresentationFormProps {
  editable?: boolean
  permissionToEditQuote?: boolean
  permissionToEditPresentationSection?: boolean
  onClick?: (event: React.MouseEvent) => any
}

function QuotePresentationForm({
  onClick,
  permissionToEditQuote,
  permissionToEditPresentationSection,
  ...props
}: QuotePresentationFormProps) {
  const classes = useStyles(props)
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const { quote, updateQuote, onPresentationPageChange, presentationPage, isDirty } = useContext(QuoteContext)
  const { canDeactivateSettingsItems } = useFlags()
  const template = getQuoteTemplateOptions({ options, canDeactivate: !!canDeactivateSettingsItems }).find(
    (template) => template.key === quote._id
  )
  const [presentation, setPresentation] = useState(
    getQuotePresentation({ options, quote, inactive: true, isWorkOrder: false, isProductOrderForm: false })
  )
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const isSmartTemplate = quote.smartTemplate && quote.type === 'quoteTemplate'

  const presentationOptions = getPresentationOptions({ options })

  const canChangePresentation =
    !quote?.is_invoice || getFeature({ options, path: 'presentation.presentationsOnInvoices' })

  console.log({ canChangePresentation, options: options.options.features.presentation })

  function PresentationPage({ page, pageIndex }: { page: PresentationPage; pageIndex: number }) {
    const sections = page.sections
      .filter((section) => section?.advanced?.allowCustom)
      .map((section) => {
        const label = section?.title ? section?.title : section.label

        return {
          ...section,
          label
        }
      })

    return (
      <>
        <Grid container>
          <Grid item xs={12}>
            <Grid container alignItems={'center'} style={{ wordWrap: 'break-word' }}>
              <Grid item xs={9}>
                <Button
                  disableRipple={true}
                  classes={{
                    root: classnames({
                      [classes.button]: true,
                      [classes.currentPage]: isCurrentItem(page, pageIndex, presentationPage)
                      // [classes.currentSection]: isSinglePage && isCurrentItem(item, index, currentSection)
                    })
                  }}
                  variant={'text'}
                  disabled={isSmartTemplate}
                  onClick={(_) => {
                    onPresentationPageChange(page.key)
                    // if (isSinglePage && onSectionClick) {
                    //   onSectionClick(ev, item.key)
                    // } else if (onPageClick) {
                    //   onPageClick(ev, item.key)
                    // }
                  }}
                >
                  <div className={classes.textDiv}>
                    <Typography noWrap style={{ whiteSpace: 'pre-wrap' }} variant={'h5'}>
                      {page.title}
                    </Typography>
                  </div>
                </Button>
              </Grid>

              <Grid item xs={3}>
                <Switch
                  checked={page?.active ?? true}
                  classes={{ root: classnames({ [classes.hidden]: !page?.advanced?.allowCustom }) }}
                  disabled={!page?.advanced?.allowCustom || !permissionToEditQuote}
                  onChange={handlePageActiveChange}
                />
              </Grid>
            </Grid>
          </Grid>
          {sections.length > 0 && permissionToEditPresentationSection && (
            <Grid item xs={12}>
              <OptionsTileList
                title={''}
                classes={{ tiles: classes.tiles }}
                items={sections}
                disabled={isSmartTemplate}
                onEditItem={handleEditSection}
                axis={'y'}
              />
            </Grid>
          )}
        </Grid>
      </>
    )

    function handlePageActiveChange() {
      const updatedPages = [...presentation.pages]

      updatedPages[pageIndex].active = !updatedPages[pageIndex].active

      const updatedQuote = {
        ...quote,
        presentationTemplate: {
          ...quote.presentationTemplate,
          pages: updatedPages,
          custom: isDirty
        }
      }

      updateQuote({
        quote: updatedQuote
      })
    }

    function handleEditSection(event: React.MouseEvent, key: string) {
      const index = sections.findIndex((s) => s.key === key)
      openDialog(EditPresentationSectionDialog, {
        isCustom: quote?.presentationTemplate?.custom ?? false,
        isTemplate: quote.type === 'quoteTemplate',
        section: sections[index],
        quote: quote,
        onConfirm: (section: PresentationSection, isDirty: boolean) => {
          const updatedPages = [...presentation.pages]
          const newIndex = findIndex(updatedPages[pageIndex].sections, { key })

          updatedPages[pageIndex].sections[newIndex] = section

          const updatedQuote = {
            ...quote,
            presentationTemplate: {
              ...quote.presentationTemplate,
              custom: isDirty,
              pages: updatedPages
            }
          }

          updateQuote({
            quote: updatedQuote
          })

          dismissDialog()
        },
        onCancel: dismissDialog
      })
    }
  }

  function handleChangePresentation(_: React.MouseEvent) {
    openDialog(PresentationSelectDialog, {
      isTemplate: quote.type === 'quoteTemplate',
      quoteType: getQuoteType({ options, quote }),
      currentPresentationKey: presentation.key,
      onConfirm: (newPresentation: PresentationOption) => {
        const quoteOptionType = getQuoteTypeOption({ options, quoteType: quote.int_ext.value })
        if (quote.smartTemplate && newPresentation.key !== quoteOptionType?.defaultPresentation) {
          newPresentation.templateCustom = true
        }
        if (presentation.key === newPresentation.key) {
          setPresentation(newPresentation)
          updateQuote({
            quote: {
              ...quote,
              presentationTemplate: newPresentation
            }
          })
          dismissAllDialogs()
        } else {
          openDialog(ConfirmationDialog, {
            title: 'Are you sure?',
            message: `Any sections customized for this ${
              getObjectLabels({ quote, options }).quote.value
            } will be lost.`,
            onConfirm: () => {
              setPresentation(newPresentation)
              updateQuote({
                quote: {
                  ...quote,
                  presentationTemplate: newPresentation
                }
              })
              dismissAllDialogs()
            },
            onCancel: dismissDialog
          })
        }
      },
      onCancel: dismissDialog
    })
  }

  function handleActiveChange() {
    const updatedQuote = {
      ...quote,
      presentationTemplate: {
        ...presentation,
        active: !presentation?.active,
        custom: isDirty
      }
    }
    setPresentation(updatedQuote.presentationTemplate)
    updateQuote({
      quote: updatedQuote
    })
  }

  return (
    <div className={classes.root} onClick={onClick}>
      <div className={classes.meta}>
        <Grid container spacing={1} alignItems={'center'}>
          <Grid item xs={12}>
            <Typography variant={'h4'}>Current: {presentation.label}</Typography>
          </Grid>
          <Grid item xs={12} md={7}>
            <Grid container alignItems={'center'}>
              <Grid item>
                <Typography variant={'h6'}>{'Active'}</Typography>
              </Grid>

              {presentationOptions.length > 1 && (
                <Grid item>
                  <Switch
                    checked={presentation?.active}
                    disabled={!permissionToEditQuote}
                    onChange={handleActiveChange}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} md={5}>
            {presentationOptions.length > 1 && canChangePresentation && (
              <Button variant={'text'} disabled={!permissionToEditQuote} onClick={handleChangePresentation}>
                Change
              </Button>
            )}
          </Grid>
          {template?.smartTemplate && (
            <Typography variant={'subtitle1'}>
              {presentation?.templateCustom
                ? 'Presentation set by template'
                : `Set by ${objectLabels.quote.value} type by default`}
            </Typography>
          )}
          {presentation.templateCustom && (
            <>
              <Grid item xs={12} md={5}>
                {presentationOptions.length > 1 && (
                  <Button
                    size={'small'}
                    variant={'text'}
                    disabled={!permissionToEditQuote}
                    onClick={() => {
                      openDialog(ConfirmationDialog, {
                        title: 'Are you sure?',
                        message: `Any sections customized for this ${
                          getObjectLabels({ quote, options }).quote.value
                        } will be lost.`,
                        onConfirm: () => {
                          const defaultPresentation = getInitialPresentation({
                            options,
                            quoteType: getQuoteTypeOption({ options, quoteType: quote.int_ext.value })
                          })
                          setPresentation(defaultPresentation)
                          updateQuote({
                            quote: {
                              ...quote,
                              presentationTemplate: defaultPresentation
                            }
                          })
                          dismissAllDialogs()
                        },
                        onCancel: dismissDialog
                      })
                    }}
                  >
                    Use Default
                  </Button>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </div>
      <div className={classes.pages}>
        {presentation.pages.map((page, index) => (
          <Grid container spacing={3} key={index}>
            <Grid item xs={12}>
              <PresentationPage page={page} pageIndex={index} />
            </Grid>
          </Grid>
        ))}
      </div>
    </div>
  )

  function isCurrentItem(item: PresentationPage | PresentationSection, index: number, currentKey: string) {
    return (currentKey === null && index === 0) || item.key === currentKey
  }
}

export default QuotePresentationForm
